import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { measurementsApi } from '@/shared/api/endpoints';
import type { BodyMeasurement, DerivedValue } from '@/shared/api/types';
import { formatDayShort, todayIso } from '@/shared/format/dates';
import { formatGrams, formatRatio, formulaLabel } from '@/shared/format/nutrition';
import { describeError, useAsync } from '@/shared/hooks/useAsync';
import { Banner, Button, Card, EmptyState, Eyebrow, Field, Sheet } from '@/shared/ui';
import { BodyIcon } from '@/shared/ui/icons';
import { EsqueletoLista, useEsqueleto } from '@/shared/ui/Skeleton';
import { Sparkline } from './Sparkline';

/**
 * S-08 · el cuerpo.
 *
 * Las métricas derivadas se muestran **con su fórmula** (RF-38/RF-39): son valores calculados, no
 * medidos, y presentarlos como dato propio los volvería incuestionables. El sistema no repite
 * ninguna cuenta: llegan del servidor con la fórmula y la versión con las que se obtuvieron.
 */
export function MeasurementsPage() {
  const history = useAsync<BodyMeasurement[]>(() => measurementsApi.history(), []);
  const [recording, setRecording] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);

  const measurements = [...(history.data ?? [])].sort((a, b) => a.date.localeCompare(b.date));
  const latest = measurements[measurements.length - 1] ?? null;
  const earliest = measurements[0] ?? null;
  const change =
    latest !== null && earliest !== null && measurements.length > 1
      ? latest.weightKg - earliest.weightKg
      : null;

  const esqueleto = useEsqueleto(history.loading);

  return (
    <>
      <header className="row-between">
        <div>
          {latest !== null && <Eyebrow>Última carga · {formatDayShort(latest.date)}</Eyebrow>}
          <h1>Tu cuerpo</h1>
        </div>
        <Button variant="quiet" onClick={() => setRecording(true)}>
          Cargar hoy
        </Button>
      </header>

      {warning !== null && (
        // RN-15 · el objetivo se fijó con otro peso. Se avisa; recalcular es decisión de la persona.
        <Banner tone="decision">
          {warning} <Link to="/objetivos">Recalcular</Link>
        </Banner>
      )}

      {esqueleto && history.data === null && (
        <EsqueletoLista etiqueta="Cargando las mediciones" filas={4} />
      )}
      {history.error !== null && <Banner tone="danger">{history.error}</Banner>}

      {history.data !== null && measurements.length === 0 && (
        <EmptyState icon={<BodyIcon size={64} />} title="Todavía no hay mediciones">
          <p>
            Con el peso alcanza para calcular tu gasto. La cintura y el cuello agregan la
            composición corporal, si te interesa.
          </p>
          <Button variant="primary" onClick={() => setRecording(true)}>
            Cargar la primera
          </Button>
        </EmptyState>
      )}

      {latest !== null && (
        <Card size="lg">
          <div className="row-between">
            <div className="figure figure-item">
              <span className="figure-value">{formatGrams(latest.weightKg)}</span>
              <span className="figure-unit">kg</span>
            </div>

            {change !== null && earliest !== null && (
              <div className="stack" style={{ gap: 2, textAlign: 'right' }}>
                {/* Sin juicio sobre la dirección: el signo es un hecho, no una nota. */}
                <span className="small numeric" style={{ fontWeight: 700 }}>
                  {change > 0 ? '+' : ''}
                  {formatGrams(change)} kg
                </span>
                <span className="nano muted numeric">
                  desde {formatGrams(earliest.weightKg)} kg el {formatDayShort(earliest.date)}
                </span>
              </div>
            )}
          </div>

          <Sparkline
            points={measurements.map((entry) => ({ date: entry.date, value: entry.weightKg }))}
          />
        </Card>
      )}

      {latest?.metrics != null && (
        <div className="stack" style={{ gap: 'var(--space-2)' }}>
          <Eyebrow>Valores derivados</Eyebrow>
          <Card size="flush">
            <DerivedRow label="Índice de masa corporal" value={latest.metrics.bodyMassIndex} />
            {/*
              `measured` existe por una inconsistencia del servidor: cuando no hay cintura devuelve
              `waistToHeightRatio: { value: 0 }` en lugar de null, mientras que grasa y masa magra
              sí vienen en null. Sin esta guarda la pantalla mostraba «Cintura / altura · 0,00»
              sobre una medición donde nadie midió una cintura — un derivado sin insumo presentado
              como dato, que es exactamente lo que este producto promete no hacer.
            */}
            <DerivedRow
              label="Cintura / altura"
              value={latest.metrics.waistToHeightRatio}
              measured={latest.waistCm !== null}
              ratio
            />
            <DerivedRow label="Grasa corporal" value={latest.metrics.bodyFatPercentage} unit="%" />
            <DerivedRow label="Masa magra" value={latest.metrics.fatFreeMassKg} unit="kg" />
          </Card>
          <p className="micro muted">
            Son valores calculados, no medidos: se muestran con su fórmula y no se guardan como dato
            propio.
          </p>
        </div>
      )}

      {latest !== null &&
        (latest.waistCm !== null || latest.hipCm !== null || latest.neckCm !== null) && (
          <div className="stack" style={{ gap: 'var(--space-2)' }}>
            <Eyebrow>Circunferencias</Eyebrow>
            <div className="macro-grid">
              {[
                ['Cintura', latest.waistCm],
                ['Cadera', latest.hipCm],
                ['Cuello', latest.neckCm],
              ].map(([label, value]) => (
                <div key={label as string} className="macro">
                  <span className="macro-name">{label}</span>
                  <span className="macro-value">
                    {value === null ? '—' : `${formatGrams(value as number)} cm`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

      {/* La barra inferior tiene cuatro destinos y ninguno es la cuenta: se llega desde acá. */}
      <Card size="flush">
        <Link to="/objetivos" className="option-row">
          <span className="option-title" style={{ flex: 1 }}>
            Tu objetivo
          </span>
        </Link>
        <Link to="/perfil" className="option-row">
          <span className="option-title" style={{ flex: 1 }}>
            Tu perfil
          </span>
        </Link>
        <Link to="/cuenta" className="option-row">
          <span className="option-title" style={{ flex: 1 }}>
            Tu cuenta
          </span>
        </Link>
      </Card>

      {recording && (
        <RecordSheet
          onClose={() => setRecording(false)}
          onRecorded={(recorded) => {
            if (recorded.staleGoalWarning !== null) {
              const { differenceKg } = recorded.staleGoalWarning;
              setWarning(
                `Tu objetivo se fijó con un peso ${formatGrams(Math.abs(differenceKg))} kg ` +
                  `${differenceKg > 0 ? 'mayor' : 'menor'} que el de hoy.`,
              );
            }
            history.reload();
          }}
        />
      )}
    </>
  );
}

function DerivedRow({
  label,
  value,
  unit,
  measured = true,
  ratio = false,
}: {
  label: string;
  value: DerivedValue | null;
  unit?: string;
  /** Falso cuando falta la medición de la que se deriva, aunque el servidor mande un número. */
  measured?: boolean;
  /** Un cociente necesita dos decimales: 0,45 y 0,48 no son la misma lectura. */
  ratio?: boolean;
}) {
  if (value === null || !measured) {
    return null;
  }

  return (
    <div className="option-row" style={{ cursor: 'default' }}>
      <span className="stack" style={{ flex: 1, gap: 2 }}>
        <span className="option-title">{label}</span>
        <span className="option-hint">{formulaLabel(value.formula, value.version)}</span>
      </span>
      <span className="item-kcal">
        {ratio ? formatRatio(value.value) : formatGrams(value.value)} {unit ?? ''}
      </span>
    </div>
  );
}

function RecordSheet({
  onClose,
  onRecorded,
}: {
  onClose: () => void;
  onRecorded: (measurement: BodyMeasurement) => void;
}) {
  const [date, setDate] = useState(todayIso());
  const [weightKg, setWeightKg] = useState('');
  const [waistCm, setWaistCm] = useState('');
  const [hipCm, setHipCm] = useState('');
  const [neckCm, setNeckCm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      onRecorded(
        await measurementsApi.record({
          date,
          weightKg: Number(weightKg),
          waistCm: waistCm === '' ? null : Number(waistCm),
          hipCm: hipCm === '' ? null : Number(hipCm),
          neckCm: neckCm === '' ? null : Number(neckCm),
          replaceExisting: true,
        }),
      );
      onClose();
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet title="Cargar una medición" onClose={onClose}>
      <form className="stack" onSubmit={(event) => void submit(event)}>
        {error !== null && <Banner tone="danger">{error}</Banner>}

        <Field
          label="Fecha"
          type="date"
          max={todayIso()}
          value={date}
          onChange={(event) => setDate(event.target.value)}
        />
        <Field
          label="Peso (kg)"
          type="number"
          inputMode="decimal"
          min={20}
          max={400}
          step="0.1"
          required
          autoFocus
          value={weightKg}
          onChange={(event) => setWeightKg(event.target.value)}
        />
        <Field
          label="Cintura (cm)"
          type="number"
          inputMode="decimal"
          min={0}
          step="0.1"
          value={waistCm}
          onChange={(event) => setWaistCm(event.target.value)}
          hint="Opcional. Con cintura y cuello se puede estimar el porcentaje de grasa."
        />
        <Field
          label="Cadera (cm)"
          type="number"
          inputMode="decimal"
          min={0}
          step="0.1"
          value={hipCm}
          onChange={(event) => setHipCm(event.target.value)}
          hint="Opcional."
        />
        <Field
          label="Cuello (cm)"
          type="number"
          inputMode="decimal"
          min={0}
          step="0.1"
          value={neckCm}
          onChange={(event) => setNeckCm(event.target.value)}
          hint="Opcional."
        />

        <Button type="submit" variant="primary" block disabled={submitting || weightKg === ''}>
          {submitting ? 'Guardando…' : 'Registrar medición'}
        </Button>
        <p className="micro muted">Si ya habías cargado una medición ese día, esta la reemplaza.</p>
      </form>
    </Sheet>
  );
}
